<template>
    <vx-modal v-model:visible="visible" size="sm" padding="standard">
        <template v-slot:header>Suspend Team</template>
        <template v-slot:subheader>{{ team.name }}</template>

        <div class="text-sm text-slate-800">
            <div class="mb-2">
                <strong>Are you sure you want to proceed?</strong> If you continue the following will happen:
            </div>
            <ul class="list-disc flex flex-col gap-1 pl-6 mb-8">
                <li>All Owners and Members will have read only capabilities.</li>
                <li>All outbound messaging will be stopped.</li>
            </ul>

            <vx-label size="md">Why is the team being suspended?</vx-label>
            <vx-radio-selection size="sm" v-model="selectedReason">
                <vx-radio-selection-item value="Invalid Payment">Invalid Payment</vx-radio-selection-item>
                <vx-radio-selection-item value="Churn">Churn</vx-radio-selection-item>
                <vx-radio-selection-item value="custom">Custom</vx-radio-selection-item>
            </vx-radio-selection>

            <div v-if="selectedReason === 'custom'" class="mt-6">
                <vx-label size="md">Custom Reason</vx-label>
                <vx-input v-model="reason" size="lg" :rows="4" maxlength="250"></vx-input>
            </div>
        </div>

        <template v-slot:footer>
            <div class="flex flex-col-reverse gap-4 lg:flex-row">
                <vx-button
                    @click="visible = false"
                    type="button"
                    class="grow lg:w-20 lg:grow-0"
                    size="lg"
                    color="muted"
                >
                    Cancel
                </vx-button>
                <vx-button
                    @click="confirm()"
                    data-test="confirm-suspension"
                    :disabled="!reason"
                    :loading="suspending"
                    type="button"
                    class="grow"
                    size="lg"
                    color="primary"
                >
                    Confirm
                </vx-button>
            </div>
        </template>
    </vx-modal>
</template>

<script setup>
import {
    VxButton,
    VxModal,
    VxRadioSelection,
    VxRadioSelectionItem,
    VxLabel,
    VxInput,
} from '@voxie/frontend-components';
import { ref, watch } from 'vue';
import { HTTP_INTERNAL_SERVER_ERROR } from '~/support/HttpStatuses';
import { useToasted } from '~/composables/useToasted';
import { adminTeamsClient } from '~/services';

const toasted = useToasted();

const visible = defineModel('visible', {
    type: Boolean,
    default: false,
});

const team = defineModel('team', {
    type: Object,
    required: true,
});

const selectedReason = ref('');
const reason = ref('');

watch(selectedReason, () => {
    reason.value = selectedReason.value === 'custom' ? '' : selectedReason.value;
});

const suspending = ref(false);

const confirm = async () => {
    if (suspending.value) {
        return;
    }

    try {
        suspending.value = true;

        await adminTeamsClient.suspendTeam(team.value.id, {
            reason: reason.value,
        });

        visible.value = false;
        team.value.status = 'suspended';
        team.value.status_reason = reason.value;
    } catch (e) {
        console.error(e);
        if (e?.response?.status >= HTTP_INTERNAL_SERVER_ERROR) {
            toasted.global.platform_error();
            return;
        }

        toasted.error(e.response?.data?.message || 'Something went wrong.');
    } finally {
        suspending.value = false;
    }
};
</script>
